import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const translationModules = import.meta.glob('../../locales/**/*.json', {
    eager: true,
    import: 'default',
}) as Record<string, Record<string, unknown>>;

// 語言選項類型
interface LanguageOption {
    code: string;
    name: string;
}

// 翻譯資源類型
interface TranslationResources {
    [language: string]: {
        [namespace: string]: Record<string, unknown>;
    };
}

// i18next 配置類型
interface I18nConfig {
    resources: TranslationResources;
    ns: string[];
    defaultNS: string;
    fallbackLng: string;
    supportedLngs: string[];
    detection: {
        order: string[];
        lookupQuerystring: string;
        lookupCookie: string;
        lookupLocalStorage: string;
        caches: string[];
    };
    interpolation: {
        escapeValue: boolean;
    };
}

// 語言配置
export const languageOptions: LanguageOption[] = [
    { code: 'zh-TW', name: '繁體中文' },
    { code: 'en', name: 'English' },
    // 以下與後台 sys_language 的活動語系一致
    { code: 'zh-CN', name: '简体中文' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
    { code: 'vi', name: 'Tiếng Việt' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'th', name: 'ไทย' },
    { code: 'fil', name: 'Filipino' },
    { code: 'ms', name: 'Bahasa Melayu' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'pt', name: 'Português' },
    { code: 'it', name: 'Italiano' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ru', name: 'Русский' },
    { code: 'sv', name: 'Svenska' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'ar', name: 'العربية' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'uk', name: 'Українська' },
];

// 從文件路徑中提取語言和命名空間
// 支持嵌套文件夾結構，例如：
// locales/zh-TW/common.json -> { lang: 'zh-TW', ns: 'common' }
// locales/zh-TW/pps/redirect.json -> { lang: 'zh-TW', ns: 'pps_redirect' }
function parseTranslationPath(path: string): { lang: string; ns: string } | null {
    // 匹配 locales/{語言}/{路徑}/{文件名}.json
    // 例如: ../../locales/zh-TW/pps/redirect.json
    const match = path.match(/locales\/([^/]+)\/(.+?)\.json$/);
    if (!match) return null;

    const lang = match[1];
    const fullPath = match[2]; // 例如: 'pps/redirect' 或 'common'

    // 將路徑轉換為命名空間，用下劃線分隔（避免與 i18next 的命名空間語法衝突）
    // 'pps/redirect' -> 'pps_redirect'
    // 'common' -> 'common'
    const ns = fullPath.replace(/\//g, '_');

    return {
        lang,
        ns,
    };
}

// 自動構建翻譯資源（只收 languageOptions 開放的語系）
function buildTranslationResources(): TranslationResources {
    const resources: TranslationResources = {};
    const enabled = new Set(languageOptions.map(o => o.code));

    // 遍歷所有加載的翻譯模組
    Object.entries(translationModules).forEach(([path, translations]) => {
        const parsed = parseTranslationPath(path);
        if (!parsed) return;

        const { lang, ns } = parsed;
        if (!enabled.has(lang)) return;

        // 初始化語言資源對象
        if (!resources[lang]) {
            resources[lang] = {};
        }

        // 添加命名空間資源
        resources[lang][ns] = translations;
    });

    return resources;
}

// 構建翻譯資源
const resources = buildTranslationResources();

// 獲取所有命名空間（按字母順序排序）
const namespaces = Array.from(
    new Set(Object.values(resources).flatMap(lang => Object.keys(lang)))
).sort();

// 初始化 i18next
const config: I18nConfig = {
    resources,
    ns: namespaces,
    // 不指定的話 i18next 取 ns 陣列第一個，而 namespaces 經 sort() 後首位是 cgs_redirect，
    // 會讓未帶 ns 的 key 落到不相干的 namespace
    defaultNS: 'common',
    fallbackLng: 'zh-TW',
    // 可用語言以 languageOptions 為準：不鎖的話瀏覽器語言偵測到沒有翻譯檔的語系也會選上，
    // 缺的 ns 會顯示原文 key。新增語系前先比對 locales/zh-TW 的 key 是否補齊
    // 不可加 nonExplicitSupportedLngs：它會把 'zh-TW' 正規化成 'zh' 去比對而落空
    supportedLngs: languageOptions.map(o => o.code),
    detection: {
        order: ['querystring', 'cookie', 'localStorage', 'navigator'],
        lookupQuerystring: 'lng',
        lookupCookie: 'i18next',
        lookupLocalStorage: 'i18nextLng',
        caches: ['localStorage', 'cookie'],
    },
    interpolation: {
        escapeValue: false,
    },
};

i18next.use(LanguageDetector).init(config);

// 在 <html> 標上目前 UI 語系，給只限某語系的樣式用（html lang 固定 zh-TW，不能拿來判斷）：
// data-ui-lng 是語系代碼；data-ui-script 分中日韓（cjk）與其他（alphabetic），字較長的拼音文字共用的版面調整看這個
const markUiLanguage = (lng: string): void => {
    if (typeof document === 'undefined') return;
    const { dataset } = document.documentElement;
    dataset.uiLng = lng;
    dataset.uiScript = /^(zh|ja|ko)\b/i.test(lng) ? 'cjk' : 'alphabetic';
};
markUiLanguage(i18next.language);
i18next.on('languageChanged', markUiLanguage);

// 輔助函數：解析帶命名空間的翻譯鍵
// 例如: 'pps_redirect:label.title' -> { ns: 'pps_redirect', key: 'label.title' }
export function parseTranslationKey(key: string): { ns: string | undefined; key: string } {
    // 檢查是否包含命名空間（格式：namespace:key）
    const colonIndex = key.indexOf(':');
    if (colonIndex !== -1) {
        const ns = key.substring(0, colonIndex);
        const actualKey = key.substring(colonIndex + 1);
        return { ns, key: actualKey };
    }
    return { ns: undefined, key };
}

// 封裝翻譯函數，支持嵌套命名空間
export function translate(key: string, options?: { [key: string]: unknown }): string {
    const parsed = parseTranslationKey(key);
    if (parsed.ns) {
        return i18next.t(parsed.key, { ...options, ns: parsed.ns });
    }
    return i18next.t(key, options);
}

export default i18next;
export type { LanguageOption, TranslationResources, I18nConfig };
